import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, Mail } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/ui/Button';
import livePulseLogo from '../assets/live-pulse-logo.png';
import { API_PATH, request } from '../config/api';
import styles from './Login.module.css';

const VerifyEmailPage = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading | success | error
    const [message, setMessage] = useState('');
    const [details, setDetails] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(search);
        const token = params.get('token');
        if (!token) {
            setStatus('error');
            setMessage('Verification link is missing or invalid.');
            setDetails('Try opening the link directly from the email we sent you, or request a new verification email from your dashboard.');
            return;
        }
        let cancelled = false;
        const verify = async () => {
            setStatus('loading');
            setMessage('');
            setDetails('');
            try {
                const res = await request(`${API_PATH}/auth/verify-email?token=${encodeURIComponent(token)}`);
                const data = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (res.ok) {
                    setStatus('success');
                    setMessage(data.message || 'Email verified successfully. You can now sign in.');
                    setDetails('You can safely close this tab or go to the dashboard. Your account is now active.');
                } else {
                    setStatus('error');
                    setMessage(data.error || 'Verification link is invalid or has expired.');
                    setDetails('If this link is old, sign in with the same email on LivePulse and click “Resend verification email” from your dashboard.');
                }
            } catch {
                if (cancelled) return;
                setStatus('error');
                setMessage('Network error. Please try again in a moment.');
                setDetails('Check your internet connection and refresh this page, or open the verification link again from your email.');
            }
        };
        verify();
        return () => {
            cancelled = true;
        };
    }, [search]);

    const isLoading = status === 'loading';
    const isSuccess = status === 'success';

    const statusLabel = useMemo(() => {
        if (isLoading) return 'Verifying…';
        if (isSuccess) return 'Verified';
        return 'Verification issue';
    }, [isLoading, isSuccess]);

    return (
        <div className={styles.page}>
            {/* Left brand panel (reuse Login layout for consistency) */}
            <div className={styles.leftPanel}>
                <div className={styles.orb1} />
                <div className={styles.orb2} />
                <div className={styles.orb3} />

                <Link to="/" className={styles.brandLink}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.brandName} />
                </Link>

                <div className={styles.leftContent}>
                    <div className={styles.leftBadge}>
                        <Mail size={13} />
                        <span>Email verification</span>
                    </div>

                    <h2 className={styles.leftTitle}>
                        Secure your
                        <br />
                        <span className={styles.leftTitleGrad}>LivePulse account</span>
                    </h2>

                    <p className={styles.leftDesc}>
                        We verify your email once to keep your workspace safe from temporary or fake accounts.
                    </p>
                </div>

                <div className={styles.leftFooter}>© {new Date().getFullYear()} LivePulse. All rights reserved.</div>
            </div>

            {/* Right panel */}
            <div className={styles.rightPanel}>
                <div className={styles.themeBtn}><ThemeToggle /></div>

                <div className={styles.formCard}>
                    {!isLoading && isSuccess && (
                        <span className={`${styles.verifiedSticker} sticker-badge`}>
                            <CheckCircle2 size={12} /> Verified
                        </span>
                    )}

                    <div className={styles.formHeader}>
                        <h1 className={styles.formTitle}>Verify your email</h1>
                        <p className={styles.formSubtitle}>{statusLabel}</p>
                    </div>

                    <div
                        className={[
                            styles.statusCard,
                            isSuccess && styles.statusCardSuccess,
                            !isLoading && !isSuccess && styles.statusCardError,
                        ].filter(Boolean).join(' ')}
                    >
                        <div className={styles.statusIconWrap}>
                            {isLoading && <Loader2 size={28} className={styles.spinner} />}
                            {!isLoading && isSuccess && <CheckCircle2 size={28} className={styles.successIcon} />}
                            {!isLoading && !isSuccess && <AlertTriangle size={28} className={styles.errorIcon} />}
                        </div>
                        <div className={styles.statusContent}>
                            <h2 className={styles.statusTitle}>
                                {isLoading && 'We’re confirming your email…'}
                                {isSuccess && 'Your email is verified'}
                                {!isLoading && !isSuccess && 'We couldn’t verify this link'}
                            </h2>
                            <p className={styles.statusMessage}>
                                {message || 'This usually takes just a moment. Please keep this tab open while we verify your link.'}
                            </p>
                            {!isLoading && details && (
                                <p className={styles.statusDetails}>
                                    {details}
                                </p>
                            )}
                        </div>
                    </div>

                    {status !== 'loading' && (
                        <div className={styles.primaryActionsRow}>
                            <Button
                                type="button"
                                className={`${styles.submitBtn} btn-pill-primary`}
                                onClick={() => navigate('/login')}
                            >
                                Go to sign in
                            </Button>
                            
                        </div>
                    )}

                    {isLoading && (
                        <p className={styles.fieldHint} style={{ marginTop: 'var(--space-4)' }}>
                            You can safely close this page after the status changes to &quot;Verified&quot;.
                        </p>
                    )}

                    {!isLoading && isSuccess && (
                        <p className={styles.fieldHint} style={{ marginTop: 'var(--space-3)' }}>
                            You&apos;re all set. Sign in to LivePulse to start creating events and engaging your audience.
                        </p>
                    )}

                    {!isLoading && status === 'error' && (
                        <p className={styles.fieldHint} style={{ marginTop: 'var(--space-3)' }}>
                            If the problem continues, sign in with the same email on LivePulse and click &quot;Resend verification email&quot; from your dashboard.
                        </p>
                    )}

                    {!isLoading && (
                        <div className={styles.secondaryActionsRow}>
                            <button
                                type="button"
                                className={styles.termsLink}
                                onClick={() => window.location.reload()}
                            >
                                Refresh status
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmailPage;

